import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { FunctionComponent, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { Sidebar } from "primereact/sidebar";
import * as XLSX from "xlsx";
import { handlingUrlParams } from "Utils/AppUtils";
import InvestorMappingService from "services/investor-mapping-service";
import { setToaster } from "common/toastStore";

interface MersInvestorConfigSettingsProps {}

interface InvestorMapping {
  id: number;
  name: string;
  lienType: string;
  actionType: string;
  transferPeriod: string;
  transferCode: string;
  servicerOrganizationId: string;
  subServicerOrganizationId: string;
  investorOrganizationId: string;
  transferControllerOrganizationId: string;
  transferLocationOrganizationId: string;
  recipientOrganizationId: string;
  delegateOrganizationId: string;
  isCLSInvestor: boolean;
}

interface TableColumnConfig {
  field: keyof InvestorMapping;
  header: string;
}

interface MersFilters {
  name: string;
  lienType: string | null;
  transferPeriod: string | null;
  isCLSInvestor: boolean | null;
}

interface AddMappingForm {
  name: string;
  lienType: string | null;
  actionType: string | null;
  transferPeriod: string | null;
  transferCode: string;
  servicerOrganizationId: string;
  subServicerOrganizationId: string;
  investorOrganizationId: string;
  transferControllerOrganizationId: string;
  transferLocationOrganizationId: string;
  recipientOrganizationId: string;
  delegateOrganizationId: string;
  isCLSInvestor: boolean;
}

const DEFAULT_FILTERS: MersFilters = {
  name: "",
  lienType: null,
  transferPeriod: null,
  isCLSInvestor: null,
};

const LIEN_TYPE_OPTIONS = [
  { label: "FirstLien", value: "FirstLien" },
  { label: "SecondLien", value: "SecondLien" },
];

const TRANSFER_PERIOD_OPTIONS = [
  { label: "Daily", value: "Daily" },
  { label: "Monthly", value: "Monthly" },
  { label: "Period", value: "Period" },
];

const ADD_LIEN_TYPE_OPTIONS = [
  { label: "FirstLien (01)", value: "FirstLien" },
  { label: "SecondLien (02)", value: "SecondLien" },
];

const ACTION_TYPE_OPTIONS = [
  { label: "ServiceTransfer", value: "ServiceTransfer" },
  { label: "TransferOfControl", value: "TransferOfControl" },
];

const CLS_INVESTOR_OPTIONS = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

const APPLICATION_NAMES: Record<number, string> = {
  1: "SnapDoc",
  9: "Mers Registration",
  10: "Mers Transfer",
};

const buildDefaultAddForm = (applicationIdNumber: number): AddMappingForm => {
  if (applicationIdNumber === 10) {
    return {
      name: "",
      lienType: null,
      actionType: "Transfer",
      transferPeriod: null,
      transferCode: "207",
      servicerOrganizationId: "",
      subServicerOrganizationId: "",
      investorOrganizationId: "",
      transferControllerOrganizationId: "",
      transferLocationOrganizationId: "",
      recipientOrganizationId: "",
      delegateOrganizationId: "",
      isCLSInvestor: false,
    };
  }

  if (applicationIdNumber === 1) {
    return {
      name: "",
      lienType: null,
      actionType: "ServiceTransfer",
      transferPeriod: "Daily",
      transferCode: "501",
      servicerOrganizationId: "",
      subServicerOrganizationId: "",
      investorOrganizationId: "",
      transferControllerOrganizationId: "",
      transferLocationOrganizationId: "",
      recipientOrganizationId: "",
      delegateOrganizationId: "",
      isCLSInvestor: false,
    };
  }

  return {
    name: "",
    lienType: null,
    actionType: "Register",
    transferPeriod: "Daily",
    transferCode: "102",
    servicerOrganizationId: "1003924",
    subServicerOrganizationId: "",
    investorOrganizationId: "1003924",
    transferControllerOrganizationId: "",
    transferLocationOrganizationId: "",
    recipientOrganizationId: "",
    delegateOrganizationId: "",
    isCLSInvestor: false,
  };
};

const MersInvestorConfigSettings: FunctionComponent<
  MersInvestorConfigSettingsProps
> = () => {
  const { applicationId } = useParams();
  const applicationIdNumber = Number(applicationId);
  const [loading, setLoading] = useState<boolean>(false);
  const [serviceTransferLoading, setServiceTransferLoading] =
    useState<boolean>(false);
  const [transferOfControlLoading, setTransferOfControlLoading] =
    useState<boolean>(false);
  const [rows, setRows] = useState<InvestorMapping[]>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [serviceTransferRows, setServiceTransferRows] = useState<
    InvestorMapping[]
  >([]);
  const [serviceTransferTotalRecords, setServiceTransferTotalRecords] =
    useState<number>(0);
  const [transferOfControlRows, setTransferOfControlRows] = useState<
    InvestorMapping[]
  >([]);
  const [transferOfControlTotalRecords, setTransferOfControlTotalRecords] =
    useState<number>(0);
  const [filtersVisible, setFiltersVisible] = useState<boolean>(false);
  const [addVisible, setAddVisible] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editingMappingId, setEditingMappingId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [deletingMappingId, setDeletingMappingId] = useState<number | null>(
    null
  );
  const [exporting, setExporting] = useState<boolean>(false);
  const [addForm, setAddForm] = useState<AddMappingForm>(
    buildDefaultAddForm(applicationIdNumber)
  );
  const [addErrors, setAddErrors] = useState<Record<string, string>>({});
  const [filters, setFilters] = useState<MersFilters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] =
    useState<MersFilters>(DEFAULT_FILTERS);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: null,
    sortOrder: null,
  });
  const [serviceTransferLazyParams, setServiceTransferLazyParams] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: null,
    sortOrder: null,
  });
  const [transferOfControlLazyParams, setTransferOfControlLazyParams] =
    useState({
      first: 0,
      rows: 10,
      page: 1,
      sortField: null,
      sortOrder: null,
    });

  useEffect(() => {
    if (!applicationId) {
      setRows([]);
      setTotalRecords(0);
      setServiceTransferRows([]);
      setServiceTransferTotalRecords(0);
      setTransferOfControlRows([]);
      setTransferOfControlTotalRecords(0);
      return;
    }

    const queryParams: Record<string, any> = {
      ApplicationId: Number(applicationId),
    };

    if (appliedFilters.name.trim()) {
      queryParams.Name = appliedFilters.name.trim();
    }

    if (appliedFilters.lienType) {
      queryParams.LienType = appliedFilters.lienType;
    }

    if (applicationIdNumber === 10 && appliedFilters.transferPeriod) {
      queryParams.TransferPeriod = appliedFilters.transferPeriod;
    }

    if (
      (applicationIdNumber === 9 || applicationIdNumber === 10) &&
      appliedFilters.isCLSInvestor !== null
    ) {
      queryParams.IsCLSInvestor = appliedFilters.isCLSInvestor;
    }

    if (applicationIdNumber === 1) {
      setServiceTransferLoading(true);
      setTransferOfControlLoading(true);

      InvestorMappingService.getInvestorMappings(
        handlingUrlParams({
          ...queryParams,
          ActionType: "ServiceTransfer",
          TransferPeriod: "Daily",
          PageNumber: serviceTransferLazyParams.page,
          PageSize: serviceTransferLazyParams.rows,
        })
      )
        .then((response: any) => {
          setServiceTransferRows(response?.data?.data ?? []);
          setServiceTransferTotalRecords(
            response?.data?.paginationInfo?.totalCount ?? 0
          );
        })
        .catch((e: Error) => {
          console.log(e);
          setServiceTransferRows([]);
          setServiceTransferTotalRecords(0);
        })
        .finally(() => setServiceTransferLoading(false));

      InvestorMappingService.getInvestorMappings(
        handlingUrlParams({
          ...queryParams,
          ActionType: "TransferOfControl",
          TransferPeriod: "Daily",
          PageNumber: transferOfControlLazyParams.page,
          PageSize: transferOfControlLazyParams.rows,
        })
      )
        .then((response: any) => {
          setTransferOfControlRows(response?.data?.data ?? []);
          setTransferOfControlTotalRecords(
            response?.data?.paginationInfo?.totalCount ?? 0
          );
        })
        .catch((e: Error) => {
          console.log(e);
          setTransferOfControlRows([]);
          setTransferOfControlTotalRecords(0);
        })
        .finally(() => setTransferOfControlLoading(false));

      return;
    }

    setLoading(true);

    InvestorMappingService.getInvestorMappings(
      handlingUrlParams({
        ...queryParams,
        PageNumber: lazyParams.page,
        PageSize: lazyParams.rows,
      })
    )
      .then((response: any) => {
        setRows(response?.data?.data ?? []);
        setTotalRecords(response?.data?.paginationInfo?.totalCount ?? 0);
        setLoading(false);
      })
      .catch((e: Error) => {
        console.log(e);
        setLoading(false);
      });
  }, [
    applicationId,
    applicationIdNumber,
    lazyParams.page,
    lazyParams.rows,
    serviceTransferLazyParams.page,
    serviceTransferLazyParams.rows,
    transferOfControlLazyParams.page,
    transferOfControlLazyParams.rows,
    appliedFilters,
  ]);

  useEffect(() => {
    if (applicationIdNumber !== 10) {
      setFilters((prev) => ({ ...prev, transferPeriod: null }));
      setAppliedFilters((prev) => ({ ...prev, transferPeriod: null }));
    }

    if (applicationIdNumber !== 9 && applicationIdNumber !== 10) {
      setFilters((prev) => ({ ...prev, isCLSInvestor: null }));
      setAppliedFilters((prev) => ({ ...prev, isCLSInvestor: null }));
    }
  }, [applicationIdNumber]);

  const onPage = (event: any) => {
    setLazyParams((prev) => ({
      ...prev,
      first: event.first,
      rows: event.rows,
      page: event.page + 1,
    }));
  };

  const onServiceTransferPage = (event: any) => {
    setServiceTransferLazyParams((prev) => ({
      ...prev,
      first: event.first,
      rows: event.rows,
      page: event.page + 1,
    }));
  };

  const onTransferOfControlPage = (event: any) => {
    setTransferOfControlLazyParams((prev) => ({
      ...prev,
      first: event.first,
      rows: event.rows,
      page: event.page + 1,
    }));
  };

  const applyFilters = () => {
    setLazyParams((prev) => ({
      ...prev,
      page: 1,
      first: 0,
    }));
    setServiceTransferLazyParams((prev) => ({
      ...prev,
      page: 1,
      first: 0,
    }));
    setTransferOfControlLazyParams((prev) => ({
      ...prev,
      page: 1,
      first: 0,
    }));
    setAppliedFilters({
      ...filters,
      transferPeriod:
        applicationIdNumber === 10 ? filters.transferPeriod : null,
      isCLSInvestor:
        applicationIdNumber === 9 || applicationIdNumber === 10
          ? filters.isCLSInvestor
          : null,
    });
    setFiltersVisible(false);
  };

  const resetAddForm = () => {
    setAddForm(buildDefaultAddForm(applicationIdNumber));
    setAddErrors({});
  };

  const openAddPanel = () => {
    setIsEditMode(false);
    setEditingMappingId(null);
    resetAddForm();
    setAddVisible(true);
  };

  const openEditPanel = (rowData: InvestorMapping) => {
    setIsEditMode(true);
    setEditingMappingId(rowData.id);
    setAddErrors({});
    setAddForm({
      name: rowData.name || "",
      lienType: rowData.lienType || null,
      actionType:
        rowData.actionType ||
        (applicationIdNumber === 1
          ? "ServiceTransfer"
          : applicationIdNumber === 10
          ? "Transfer"
          : "Register"),
      transferPeriod:
        applicationIdNumber === 10 ? rowData.transferPeriod || null : "Daily",
      transferCode:
        rowData.transferCode ||
        (applicationIdNumber === 1
          ? rowData.actionType === "TransferOfControl"
            ? "1000"
            : "501"
          : applicationIdNumber === 10
          ? "207"
          : "102"),
      servicerOrganizationId:
        rowData.servicerOrganizationId ||
        (applicationIdNumber === 1
          ? ""
          : applicationIdNumber === 10
          ? ""
          : "1003924"),
      subServicerOrganizationId: rowData.subServicerOrganizationId || "",
      investorOrganizationId:
        rowData.investorOrganizationId ||
        (applicationIdNumber === 10 ? "" : "1003924"),
      transferControllerOrganizationId:
        rowData.transferControllerOrganizationId || "",
      transferLocationOrganizationId:
        rowData.transferLocationOrganizationId || "",
      recipientOrganizationId: rowData.recipientOrganizationId || "",
      delegateOrganizationId: rowData.delegateOrganizationId || "",
      isCLSInvestor: !!rowData.isCLSInvestor,
    });
    setAddVisible(true);
  };

  const closeAddPanel = () => {
    setAddVisible(false);
    setIsEditMode(false);
    setEditingMappingId(null);
    resetAddForm();
  };

  const handleNumericInput = (key: keyof AddMappingForm, value: string) => {
    if (/^\d*$/.test(value)) {
      setAddForm((prev) => ({ ...prev, [key]: value }));
    }
  };

  const validateAddForm = () => {
    const errors: Record<string, string> = {};

    if (!addForm.name.trim()) errors.name = "Name is required";
    if (!addForm.lienType) errors.lienType = "Lien Type is required";
    if (applicationIdNumber === 1 && !addForm.actionType)
      errors.actionType = "Action Type is required";
    if (applicationIdNumber === 10 && !addForm.transferPeriod)
      errors.transferPeriod = "Transfer Period is required";
    if (!addForm.transferCode.trim())
      errors.transferCode = "Transfer Code is required";
    if (applicationIdNumber !== 1 && !addForm.servicerOrganizationId.trim())
      errors.servicerOrganizationId = "Servicer Organization Id is required";
    if (applicationIdNumber !== 1 && !addForm.investorOrganizationId.trim())
      errors.investorOrganizationId = "Investor Organization Id is required";

    if (applicationIdNumber === 1) {
      if (!addForm.transferLocationOrganizationId.trim())
        errors.transferLocationOrganizationId =
          "Transfer Location Organization Id is required";

      if (addForm.actionType === "TransferOfControl") {
        if (!addForm.transferControllerOrganizationId.trim())
          errors.transferControllerOrganizationId =
            "Transfer Controller Organization Id is required";
      }
    }

    setAddErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getLienCode = (lienType: string | null) => {
    if (lienType === "FirstLien") return "01";
    if (lienType === "SecondLien") return "02";
    return "";
  };

  const getActionType = () => {
    if (applicationIdNumber === 1)
      return addForm.actionType || "ServiceTransfer";
    return applicationIdNumber === 10 ? "Transfer" : "Register";
  };

  const getTransferCode = () => {
    if (applicationIdNumber === 1) {
      return addForm.actionType === "TransferOfControl" ? "1000" : "501";
    }

    if (applicationIdNumber === 10) {
      return addForm.transferCode.trim();
    }

    return addForm.transferCode.trim();
  };

  const hasDuplicateMapping = async () => {
    const queryParams: Record<string, any> = {
      ApplicationId: applicationIdNumber,
      Name: addForm.name.trim(),
      LienType: addForm.lienType,
      PageNumber: 1,
      PageSize: 500,
    };

    if (applicationIdNumber === 1) {
      queryParams.ActionType = addForm.actionType;
      queryParams.TransferPeriod = "Daily";
      queryParams.TransferCode = getTransferCode();
    }

    if (applicationIdNumber === 10 && addForm.transferPeriod) {
      queryParams.TransferPeriod = addForm.transferPeriod;
      queryParams.TransferCode = addForm.transferCode.trim();
    }

    if (applicationIdNumber === 9) {
      queryParams.ActionType = "Register";
      queryParams.TransferPeriod = "Daily";
      queryParams.TransferCode = addForm.transferCode.trim();
    }

    const response = await InvestorMappingService.getInvestorMappings(
      handlingUrlParams(queryParams)
    );

    const existingRows: InvestorMapping[] = response?.data?.data ?? [];

    return existingRows.some((item) => {
      if (
        isEditMode &&
        editingMappingId !== null &&
        item.id === editingMappingId
      ) {
        return false;
      }

      const sameName =
        (item.name || "").trim().toLowerCase() ===
        addForm.name.trim().toLowerCase();
      const sameLienType =
        (item.lienType || "").trim().toLowerCase() ===
        (addForm.lienType || "").trim().toLowerCase();

      if (applicationIdNumber === 1) {
        const sameActionType =
          (item.actionType || "").trim().toLowerCase() ===
          (addForm.actionType || "").trim().toLowerCase();
        const sameTransferPeriod =
          (item.transferPeriod || "").trim().toLowerCase() === "daily";
        return sameName && sameLienType && sameActionType && sameTransferPeriod;
      }

      const sameTransferCode =
        (item.transferCode || "").trim() === addForm.transferCode.trim();

      if (applicationIdNumber === 10) {
        const sameTransferPeriod =
          (item.transferPeriod || "").trim().toLowerCase() ===
          (addForm.transferPeriod || "").trim().toLowerCase();
        return (
          sameName && sameLienType && sameTransferPeriod && sameTransferCode
        );
      }

      const sameActionType =
        (item.actionType || "").trim().toLowerCase() === "register";
      const sameTransferPeriod =
        (item.transferPeriod || "").trim().toLowerCase() === "daily";

      return sameName && sameLienType && sameActionType && sameTransferPeriod;
    });
  };

  const submitMapping = async () => {
    if (!validateAddForm()) return;

    setIsSaving(true);

    try {
      const isDuplicate = await hasDuplicateMapping();

      if (isDuplicate) {
        setToaster({
          severity: "warn",
          summary: "Duplicate mapping",
          detail:
            applicationIdNumber === 10
              ? "A mapping with the same Name, Lien Type, Transfer Period, and Transfer Code already exists."
              : "A mapping with the same Name, Lien Type, Action Type, and Transfer Period already exists.",
        });
        setIsSaving(false);
        return;
      }

      const payload = {
        ...(isEditMode && editingMappingId !== null
          ? { id: editingMappingId }
          : {}),
        name: addForm.name.trim(),
        applicationId: applicationIdNumber,
        lienType: addForm.lienType,
        lienCode: getLienCode(addForm.lienType),
        actionType: getActionType(),
        transferPeriod:
          applicationIdNumber === 10 ? addForm.transferPeriod : "Daily",
        transferCode: getTransferCode(),
        servicerOrganizationId: addForm.servicerOrganizationId.trim(),
        subServicerOrganizationId: addForm.subServicerOrganizationId.trim(),
        investorOrganizationId: addForm.investorOrganizationId.trim(),
        transferControllerOrganizationId:
          applicationIdNumber === 1 &&
          addForm.actionType === "TransferOfControl"
            ? addForm.transferControllerOrganizationId.trim()
            : "",
        transferLocationOrganizationId:
          applicationIdNumber === 1
            ? addForm.transferLocationOrganizationId.trim()
            : "",
        recipientOrganizationId:
          applicationIdNumber === 1 &&
          addForm.actionType === "TransferOfControl"
            ? addForm.recipientOrganizationId.trim()
            : "",
        delegateOrganizationId:
          applicationIdNumber === 1 &&
          addForm.actionType === "TransferOfControl"
            ? addForm.delegateOrganizationId.trim()
            : "",
        isCLSInvestor:
          applicationIdNumber === 9 || applicationIdNumber === 10
            ? addForm.isCLSInvestor
            : false,
        isActive: true,
      };

      if (isEditMode) {
        await InvestorMappingService.updateInvestorMapping(payload);
      } else {
        await InvestorMappingService.createInvestorMapping(payload);
      }

      setToaster({
        severity: "success",
        summary: "Saved",
        detail: isEditMode
          ? "Investor mapping updated successfully."
          : "Investor mapping created successfully.",
      });

      closeAddPanel();
      setAppliedFilters((prev) => ({ ...prev }));
    } catch (e: any) {
      setToaster({
        severity: "error",
        summary: "Save failed",
        detail: e?.response?.data || "Unable to create investor mapping.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const clearFilters = () => {
    const resetFilters: MersFilters = {
      ...DEFAULT_FILTERS,
      transferPeriod: applicationIdNumber === 10 ? null : null,
      isCLSInvestor:
        applicationIdNumber === 9 || applicationIdNumber === 10 ? null : null,
    };

    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setLazyParams((prev) => ({
      ...prev,
      page: 1,
      first: 0,
    }));
    setServiceTransferLazyParams((prev) => ({
      ...prev,
      page: 1,
      first: 0,
    }));
    setTransferOfControlLazyParams((prev) => ({
      ...prev,
      page: 1,
      first: 0,
    }));
  };

  const columnsForApplicationOne: TableColumnConfig[] = [
    { field: "name", header: "Name" },
    { field: "lienType", header: "Lien Type" },
    { field: "actionType", header: "Action Type" },
    { field: "servicerOrganizationId", header: "Servicer Organization Id" },
    {
      field: "subServicerOrganizationId",
      header: "Sub Servicer Organization Id",
    },
    {
      field: "transferLocationOrganizationId",
      header: "Transfer Location Organization Id",
    },
  ];

  const columnsForTransferOfControl: TableColumnConfig[] = [
    { field: "name", header: "Name" },
    { field: "lienType", header: "Lien Type" },
    { field: "actionType", header: "Action Type" },
    {
      field: "transferControllerOrganizationId",
      header: "Transfer Controller Organization Id",
    },
    {
      field: "transferLocationOrganizationId",
      header: "Transfer Location Organization Id",
    },
    { field: "recipientOrganizationId", header: "Recipient Organization Id" },
    { field: "delegateOrganizationId", header: "Delegate Organization Id" },
  ];

  const columnsForApplicationNine: TableColumnConfig[] = [
    { field: "name", header: "Name" },
    { field: "lienType", header: "Lien Type" },
    { field: "actionType", header: "Action Type" },
    { field: "transferCode", header: "Transfer Code" },
    { field: "servicerOrganizationId", header: "Servicer Organization Id" },
    {
      field: "subServicerOrganizationId",
      header: "Sub Servicer Organization Id",
    },
    { field: "investorOrganizationId", header: "Investor Organization Id" },
    { field: "isCLSInvestor", header: "Is CLS Investor" },
  ];

  const columnsForApplicationTen: TableColumnConfig[] = [
    { field: "name", header: "Name" },
    { field: "lienType", header: "Lien Type" },
    { field: "actionType", header: "Action Type" },
    { field: "transferPeriod", header: "Transfer Period" },
    { field: "transferCode", header: "Transfer Code" },
    { field: "servicerOrganizationId", header: "Servicer Organization Id" },
    {
      field: "subServicerOrganizationId",
      header: "Sub Servicer Organization Id",
    },
    { field: "investorOrganizationId", header: "Investor Organization Id" },
    { field: "isCLSInvestor", header: "Is CLS Investor" },
  ];

  const selectedColumns =
    applicationIdNumber === 1
      ? columnsForApplicationOne
      : applicationIdNumber === 9
      ? columnsForApplicationNine
      : columnsForApplicationTen;

  const exportToExcel = async () => {
    if (!applicationId) return;

    setExporting(true);

    try {
      const queryParams: Record<string, any> = {
        ApplicationId: applicationIdNumber,
      };

      if (appliedFilters.name.trim()) {
        queryParams.Name = appliedFilters.name.trim();
      }

      if (appliedFilters.lienType) {
        queryParams.LienType = appliedFilters.lienType;
      }

      if (applicationIdNumber === 10 && appliedFilters.transferPeriod) {
        queryParams.TransferPeriod = appliedFilters.transferPeriod;
      }

      if (
        (applicationIdNumber === 9 || applicationIdNumber === 10) &&
        appliedFilters.isCLSInvestor !== null
      ) {
        queryParams.IsCLSInvestor = appliedFilters.isCLSInvestor;
      }

      // Fetch every filtered record in one call so the export isn't limited to the current page.
      const response: any = await InvestorMappingService.getInvestorMappings(
        handlingUrlParams({
          ...queryParams,
          PageNumber: 1,
          PageSize: 100000,
        })
      );

      const data = response?.data?.data ?? [];

      if (!data.length) {
        setToaster({
          severity: "info",
          summary: "No data",
          detail: "No records to export.",
        });
        return;
      }

      const exportRows = data.map((item: any) => {
        const exportRow: Record<string, any> = {};
        selectedColumns.forEach((column) => {
          exportRow[column.header] = item?.[column.field];
        });
        return exportRow;
      });

      const ws = XLSX.utils.json_to_sheet(exportRows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "InvestorMappings");
      XLSX.writeFile(
        wb,
        `investor_config_settings_${applicationIdNumber}_${new Date().toISOString()}.xlsx`
      );
    } catch (e: any) {
      console.log(e);
      setToaster({
        severity: "error",
        summary: "Export failed",
        detail: e?.response?.data || "Unable to export investor mappings.",
      });
    } finally {
      setExporting(false);
    }
  };

  const renderActions = (rowData: InvestorMapping) => {
    const handleDeleteMapping = async () => {
      setDeletingMappingId(rowData.id);

      try {
        const deletePayload = {
          id: rowData.id,
          name: (rowData.name || "").trim(),
          applicationId: applicationIdNumber,
          lienType: rowData.lienType,
          lienCode: getLienCode(rowData.lienType),
          actionType:
            applicationIdNumber === 1
              ? rowData.actionType || "ServiceTransfer"
              : applicationIdNumber === 10
              ? "Transfer"
              : "Register",
          transferPeriod:
            applicationIdNumber === 10
              ? rowData.transferPeriod || "Daily"
              : "Daily",
          transferCode:
            applicationIdNumber === 1
              ? (rowData.actionType || "") === "TransferOfControl"
                ? "1000"
                : "501"
              : (rowData.transferCode || "").trim(),
          servicerOrganizationId: (rowData.servicerOrganizationId || "").trim(),
          subServicerOrganizationId: (
            rowData.subServicerOrganizationId || ""
          ).trim(),
          investorOrganizationId: (rowData.investorOrganizationId || "").trim(),
          transferControllerOrganizationId:
            applicationIdNumber === 1 &&
            (rowData.actionType || "") === "TransferOfControl"
              ? (rowData.transferControllerOrganizationId || "").trim()
              : "",
          transferLocationOrganizationId:
            applicationIdNumber === 1
              ? (rowData.transferLocationOrganizationId || "").trim()
              : "",
          recipientOrganizationId:
            applicationIdNumber === 1 &&
            (rowData.actionType || "") === "TransferOfControl"
              ? (rowData.recipientOrganizationId || "").trim()
              : "",
          delegateOrganizationId:
            applicationIdNumber === 1 &&
            (rowData.actionType || "") === "TransferOfControl"
              ? (rowData.delegateOrganizationId || "").trim()
              : "",
          isCLSInvestor:
            applicationIdNumber === 9 || applicationIdNumber === 10
              ? !!rowData.isCLSInvestor
              : false,
          isActive: false,
        };

        await InvestorMappingService.updateInvestorMapping(deletePayload);

        setToaster({
          severity: "success",
          summary: "Deleted",
          detail: "Investor mapping deleted successfully.",
        });

        setAppliedFilters((prev) => ({ ...prev }));
      } catch (e: any) {
        setToaster({
          severity: "error",
          summary: "Delete failed",
          detail: e?.response?.data || "Unable to delete investor mapping.",
        });
      } finally {
        setDeletingMappingId(null);
      }
    };

    const confirmDeleteMapping = () => {
      confirmDialog({
        message: "Are you sure you want to delete this mapping?",
        header: "Delete Confirmation",
        icon: "pi pi-exclamation-triangle",
        acceptClassName: "p-button-danger",
        accept: handleDeleteMapping,
      });
    };

    return (
      <div className="flex gap-2">
        <Button
          type="button"
          className="p-button-sm p-button-outlined p-button-rounded p-button-primary"
          icon="pi pi-pencil"
          onClick={() => openEditPanel(rowData)}
        />
        <Button
          type="button"
          className="p-button-sm p-button-outlined p-button-rounded p-button-danger"
          icon="pi pi-trash"
          onClick={confirmDeleteMapping}
          loading={deletingMappingId === rowData.id}
        />
      </div>
    );
  };

  const applicationName = APPLICATION_NAMES[applicationIdNumber] || "";

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-3">
        <div>
          <h2 className="text-xl font-bold">
            {applicationName
              ? `${applicationName} - Investor Config Settings`
              : "Investor Config Settings"}
          </h2>
        </div>

        <div className="gap-3 flex flex-row">
          {applicationIdNumber === 1 ||
          applicationIdNumber === 9 ||
          applicationIdNumber === 10 ? (
            <Button
              icon="pi pi-plus"
              label="Add New"
              onClick={openAddPanel}
              className="p-button-sm p-button-outlined p-button-success"
            />
          ) : null}
          <Button
            icon="pi pi-filter"
            label="Filters"
            onClick={() => setFiltersVisible(true)}
            className="p-button-sm p-button-outlined p-button-warning"
          />
          <Button
            icon="pi pi-times"
            label="Clear"
            onClick={clearFilters}
            className="p-button-sm p-button-outlined"
          />
          {applicationIdNumber === 9 || applicationIdNumber === 10 ? (
            <Button
              icon="pi pi-file-excel"
              label="Export to Excel"
              onClick={exportToExcel}
              loading={exporting}
              className="p-button-sm p-button-outlined"
            />
          ) : null}
        </div>
      </div>

      {applicationIdNumber === 1 ? (
        <>
          <h3 className="text-lg font-semibold mb-2">ServiceTransfer</h3>
          <DataTable
            className="shadow-md text-sm mb-4"
            value={serviceTransferRows}
            loading={serviceTransferLoading}
            stripedRows
            lazy
            paginator
            responsiveLayout="scroll"
            dataKey="id"
            first={serviceTransferLazyParams.first}
            rows={serviceTransferLazyParams.rows}
            totalRecords={serviceTransferTotalRecords}
            onPage={onServiceTransferPage}
            rowsPerPageOptions={[10, 20, 50]}
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
            emptyMessage="No service transfer mappings found."
          >
            {columnsForApplicationOne.map((column) => (
              <Column
                key={column.field}
                field={column.field}
                header={column.header}
              />
            ))}
            <Column body={renderActions} header="Options" />
          </DataTable>

          <h3 className="text-lg font-semibold mb-2">TransferOfControl</h3>
          <DataTable
            className="shadow-md text-sm"
            value={transferOfControlRows}
            loading={transferOfControlLoading}
            stripedRows
            lazy
            paginator
            responsiveLayout="scroll"
            dataKey="id"
            first={transferOfControlLazyParams.first}
            rows={transferOfControlLazyParams.rows}
            totalRecords={transferOfControlTotalRecords}
            onPage={onTransferOfControlPage}
            rowsPerPageOptions={[10, 20, 50]}
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
            emptyMessage="No transfer of control mappings found."
          >
            {columnsForTransferOfControl.map((column) => (
              <Column
                key={column.field}
                field={column.field}
                header={column.header}
              />
            ))}
            <Column body={renderActions} header="Options" />
          </DataTable>
        </>
      ) : (
        <DataTable
          className="shadow-md text-sm"
          value={rows}
          loading={loading}
          stripedRows
          lazy
          paginator
          responsiveLayout="scroll"
          dataKey="id"
          first={lazyParams.first}
          rows={lazyParams.rows}
          totalRecords={totalRecords}
          onPage={onPage}
          rowsPerPageOptions={[10, 20, 50]}
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
          emptyMessage="No investor mappings found."
        >
          {selectedColumns.map((column) => (
            <Column
              key={column.field}
              field={column.field}
              header={column.header}
            />
          ))}
          {applicationIdNumber === 9 || applicationIdNumber === 10 ? (
            <Column body={renderActions} header="Options" />
          ) : null}
        </DataTable>
      )}

      <Sidebar
        visible={filtersVisible}
        position="right"
        onHide={() => setFiltersVisible(false)}
        style={{ width: 420 }}
      >
        <div className="grid">
          <div className="col-12">
            <h3 className="text-xl font-semibold mb-3">Filters</h3>
          </div>

          <div className="col-12">
            <label className="block text-sm mb-1">Name</label>
            <InputText
              value={filters.name}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="Search by name"
              className="w-full"
            />
          </div>

          <div className="col-12">
            <label className="block text-sm mb-1">Lien Type</label>
            <Dropdown
              value={filters.lienType}
              options={LIEN_TYPE_OPTIONS}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, lienType: e.value || null }))
              }
              placeholder="Select lien type"
              className="w-full"
              showClear
            />
          </div>

          {applicationIdNumber === 10 && (
            <div className="col-12">
              <label className="block text-sm mb-1">Transfer Period</label>
              <Dropdown
                value={filters.transferPeriod}
                options={TRANSFER_PERIOD_OPTIONS}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    transferPeriod: e.value || null,
                  }))
                }
                placeholder="Select transfer period"
                className="w-full"
                showClear
              />
            </div>
          )}

          {(applicationIdNumber === 9 || applicationIdNumber === 10) && (
            <div className="col-12">
              <label className="block text-sm mb-1">Is CLS Investor</label>
              <Dropdown
                value={filters.isCLSInvestor}
                options={CLS_INVESTOR_OPTIONS}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    isCLSInvestor: e.value ?? null,
                  }))
                }
                placeholder="Select Is CLS Investor"
                className="w-full"
                showClear
              />
            </div>
          )}

          <div className="col-12 flex gap-2 mt-2">
            <Button label="Apply" icon="pi pi-filter" onClick={applyFilters} />
            <Button
              label="Clear"
              icon="pi pi-times"
              className="p-button-outlined"
              onClick={clearFilters}
            />
          </div>
        </div>
      </Sidebar>

      <Sidebar
        visible={addVisible}
        position="right"
        onHide={closeAddPanel}
        style={{ width: 480 }}
      >
        <div className="grid">
          <div className="col-12">
            <h3 className="text-xl font-semibold mb-3">
              {isEditMode ? "Edit Mapping" : "Add Mapping"}
            </h3>
          </div>

          <div className="col-12">
            <label className="block text-sm mb-1">Name *</label>
            <InputText
              value={addForm.name}
              onChange={(e) =>
                setAddForm((prev) => ({ ...prev, name: e.target.value }))
              }
              className={`w-full ${addErrors.name ? "p-invalid" : ""}`}
            />
            {addErrors.name && (
              <small className="p-error">{addErrors.name}</small>
            )}
          </div>

          <div className="col-12">
            <label className="block text-sm mb-1">Lien Type *</label>
            <Dropdown
              value={addForm.lienType}
              options={ADD_LIEN_TYPE_OPTIONS}
              onChange={(e) =>
                setAddForm((prev) => ({ ...prev, lienType: e.value || null }))
              }
              placeholder="Select lien type"
              className={`w-full ${addErrors.lienType ? "p-invalid" : ""}`}
            />
            {addErrors.lienType && (
              <small className="p-error">{addErrors.lienType}</small>
            )}
          </div>

          {applicationIdNumber === 1 && (
            <div className="col-12">
              <label className="block text-sm mb-1">Action Type *</label>
              <Dropdown
                value={addForm.actionType}
                options={ACTION_TYPE_OPTIONS}
                onChange={(e) =>
                  setAddForm((prev) => ({
                    ...prev,
                    actionType: e.value || null,
                    transferCode:
                      e.value === "TransferOfControl" ? "1000" : "501",
                  }))
                }
                placeholder="Select action type"
                className={`w-full ${addErrors.actionType ? "p-invalid" : ""}`}
              />
              {addErrors.actionType && (
                <small className="p-error">{addErrors.actionType}</small>
              )}
            </div>
          )}

          {applicationIdNumber !== 1 && (
            <div className="col-12">
              <label className="block text-sm mb-1">Transfer Code *</label>
              <InputText
                value={addForm.transferCode}
                onChange={(e) =>
                  setAddForm((prev) => ({
                    ...prev,
                    transferCode: e.target.value,
                  }))
                }
                className={`w-full ${
                  addErrors.transferCode ? "p-invalid" : ""
                }`}
              />
              {addErrors.transferCode && (
                <small className="p-error">{addErrors.transferCode}</small>
              )}
            </div>
          )}

          {applicationIdNumber === 10 && (
            <div className="col-12">
              <label className="block text-sm mb-1">Transfer Period *</label>
              <Dropdown
                value={addForm.transferPeriod}
                options={TRANSFER_PERIOD_OPTIONS}
                onChange={(e) =>
                  setAddForm((prev) => ({
                    ...prev,
                    transferPeriod: e.value || null,
                  }))
                }
                placeholder="Select transfer period"
                className={`w-full ${
                  addErrors.transferPeriod ? "p-invalid" : ""
                }`}
                showClear
              />
              {addErrors.transferPeriod && (
                <small className="p-error">{addErrors.transferPeriod}</small>
              )}
            </div>
          )}

          {applicationIdNumber === 1 && (
            <>
              {addForm.actionType === "ServiceTransfer" && (
                <>
                  <div className="col-12">
                    <label className="block text-sm mb-1">
                      Servicer Organization Id
                    </label>
                    <InputText
                      value={addForm.servicerOrganizationId}
                      onChange={(e) =>
                        handleNumericInput(
                          "servicerOrganizationId",
                          e.target.value
                        )
                      }
                      className={`w-full ${
                        addErrors.servicerOrganizationId ? "p-invalid" : ""
                      }`}
                    />
                    {addErrors.servicerOrganizationId && (
                      <small className="p-error">
                        {addErrors.servicerOrganizationId}
                      </small>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="block text-sm mb-1">
                      Sub Servicer Organization Id
                    </label>
                    <InputText
                      value={addForm.subServicerOrganizationId}
                      onChange={(e) =>
                        handleNumericInput(
                          "subServicerOrganizationId",
                          e.target.value
                        )
                      }
                      className="w-full"
                    />
                  </div>
                </>
              )}

              <div className="col-12">
                <label className="block text-sm mb-1">
                  Transfer Location Organization Id *
                </label>
                <InputText
                  value={addForm.transferLocationOrganizationId}
                  onChange={(e) =>
                    handleNumericInput(
                      "transferLocationOrganizationId",
                      e.target.value
                    )
                  }
                  className={`w-full ${
                    addErrors.transferLocationOrganizationId ? "p-invalid" : ""
                  }`}
                />
                {addErrors.transferLocationOrganizationId && (
                  <small className="p-error">
                    {addErrors.transferLocationOrganizationId}
                  </small>
                )}
              </div>

              {addForm.actionType === "TransferOfControl" && (
                <>
                  <div className="col-12">
                    <label className="block text-sm mb-1">
                      Transfer Controller Organization Id *
                    </label>
                    <InputText
                      value={addForm.transferControllerOrganizationId}
                      onChange={(e) =>
                        handleNumericInput(
                          "transferControllerOrganizationId",
                          e.target.value
                        )
                      }
                      className={`w-full ${
                        addErrors.transferControllerOrganizationId
                          ? "p-invalid"
                          : ""
                      }`}
                    />
                    {addErrors.transferControllerOrganizationId && (
                      <small className="p-error">
                        {addErrors.transferControllerOrganizationId}
                      </small>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="block text-sm mb-1">
                      Recipient Organization Id
                    </label>
                    <InputText
                      value={addForm.recipientOrganizationId}
                      onChange={(e) =>
                        handleNumericInput(
                          "recipientOrganizationId",
                          e.target.value
                        )
                      }
                      className={`w-full ${
                        addErrors.recipientOrganizationId ? "p-invalid" : ""
                      }`}
                    />
                    {addErrors.recipientOrganizationId && (
                      <small className="p-error">
                        {addErrors.recipientOrganizationId}
                      </small>
                    )}
                  </div>

                  <div className="col-12">
                    <label className="block text-sm mb-1">
                      Delegate Organization Id
                    </label>
                    <InputText
                      value={addForm.delegateOrganizationId}
                      onChange={(e) =>
                        handleNumericInput(
                          "delegateOrganizationId",
                          e.target.value
                        )
                      }
                      className={`w-full ${
                        addErrors.delegateOrganizationId ? "p-invalid" : ""
                      }`}
                    />
                    {addErrors.delegateOrganizationId && (
                      <small className="p-error">
                        {addErrors.delegateOrganizationId}
                      </small>
                    )}
                  </div>
                </>
              )}
            </>
          )}

          {applicationIdNumber !== 1 && (
            <>
              <div className="col-12">
                <label className="block text-sm mb-1">
                  Servicer Organization Id *
                </label>
                <InputText
                  value={addForm.servicerOrganizationId}
                  onChange={(e) =>
                    handleNumericInput("servicerOrganizationId", e.target.value)
                  }
                  className={`w-full ${
                    addErrors.servicerOrganizationId ? "p-invalid" : ""
                  }`}
                />
                {addErrors.servicerOrganizationId && (
                  <small className="p-error">
                    {addErrors.servicerOrganizationId}
                  </small>
                )}
              </div>

              <div className="col-12">
                <label className="block text-sm mb-1">
                  Sub Servicer Organization Id
                </label>
                <InputText
                  value={addForm.subServicerOrganizationId}
                  onChange={(e) =>
                    handleNumericInput(
                      "subServicerOrganizationId",
                      e.target.value
                    )
                  }
                  className="w-full"
                />
              </div>

              <div className="col-12">
                <label className="block text-sm mb-1">
                  Investor Organization Id *
                </label>
                <InputText
                  value={addForm.investorOrganizationId}
                  onChange={(e) =>
                    handleNumericInput("investorOrganizationId", e.target.value)
                  }
                  className={`w-full ${
                    addErrors.investorOrganizationId ? "p-invalid" : ""
                  }`}
                />
                {addErrors.investorOrganizationId && (
                  <small className="p-error">
                    {addErrors.investorOrganizationId}
                  </small>
                )}
              </div>

              {(applicationIdNumber === 9 || applicationIdNumber === 10) && (
                <div className="col-12">
                  <label className="block text-sm mb-1">Is CLS Investor</label>
                  <Dropdown
                    value={addForm.isCLSInvestor}
                    options={CLS_INVESTOR_OPTIONS}
                    onChange={(e) =>
                      setAddForm((prev) => ({
                        ...prev,
                        isCLSInvestor: e.value,
                      }))
                    }
                    className="w-full"
                  />
                </div>
              )}
            </>
          )}

          <div className="col-12 flex gap-2 mt-2">
            <Button
              label={isEditMode ? "Update" : "Save"}
              icon="pi pi-check"
              onClick={submitMapping}
              loading={isSaving}
            />
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-outlined"
              onClick={closeAddPanel}
              disabled={isSaving}
            />
          </div>
        </div>
      </Sidebar>
    </div>
  );
};

export default MersInvestorConfigSettings;
